import React from 'react';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const ContactSection = ({ contact }) => {
  const contactMethods = [
    {
      icon: Phone,
      title: 'Phone',
      value: contact.phone,
      link: `tel:${contact.phone}`,
      description: 'Mon-Sat, 9AM-6PM IST'
    },
    {
      icon: Mail,
      title: 'Email',
      value: contact.email,
      link: `mailto:${contact.email}`,
      description: 'We reply within 24 hours'
    },
    {
      icon: MapPin,
      title: 'Location',
      value: contact.address,
      link: null,
      description: 'Global shipping available'
    }
  ];

  return (
    <div className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-amber-600/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-amber-600/20 border border-amber-600/30 text-amber-100 text-sm font-semibold mb-4">
            Get In Touch
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Contact Us Today
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Ready to discuss your bitumen requirements? Our team is here to help.
          </p>
        </div>

        {/* Contact Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {contactMethods.map((method, index) => {
            const Icon = method.icon;
            return (
              <Card 
                key={index}
                className="group bg-white/10 backdrop-blur-md border-2 border-white/20 hover:border-amber-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/20 hover:-translate-y-2"
              >
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-amber-600/20 flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-600 transition-colors duration-300">
                    <Icon className="w-8 h-8 text-amber-400 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{method.title}</h3>
                  {method.link ? (
                    <a 
                      href={method.link}
                      className="text-amber-400 hover:text-amber-300 font-semibold text-lg mb-2 block transition-colors duration-300"
                    >
                      {method.value}
                    </a>
                  ) : (
                    <p className="text-amber-400 font-semibold text-lg mb-2">{method.value}</p>
                  )}
                  <p className="text-slate-400 text-sm">{method.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Business Hours */}
        <div className="text-center">
          <Card className="inline-block bg-white/5 backdrop-blur-md border-2 border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-center gap-3">
                <Clock className="w-5 h-5 text-amber-400" />
                <span className="text-slate-300 font-medium">
                  Business Hours: Monday - Saturday, 9:00 AM - 6:00 PM (IST)
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-2xl font-semibold text-white mb-4">
            Ready to get started?
          </p>
          <p className="text-slate-300 text-lg">
            Contact us today for a personalized quote and expert consultation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactSection;
